import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Heart, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ProductReviews } from "@/components/ProductReviews";
import { resolveImage } from "@/lib/product-images";
import { formatPrice } from "@/lib/format";
import { useWishlist } from "@/lib/wishlist";

const productQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["product", slug],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").eq("slug", slug).single();
      if (error) throw error;
      return data;
    },
  });

export const Route = createFileRoute("/product/$slug")({
  loader: async ({ params, context }) => {
    try {
      return await context.queryClient.ensureQueryData(productQueryOptions(params.slug));
    } catch {
      return null;
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://julsskin.shop/product/${params.slug}`;
    const p: any = loaderData ?? null;
    const fallback = params.slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const name = p?.name ?? fallback;
    const desc =
      (p?.description ?? "").trim().slice(0, 155) ||
      `${name} from Jul's Skin — premium skincare and hair care, shipped fast.`;
    const image = p?.image_url
      ? resolveImage(p.slug, p.image_url)
      : "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/a0a1439e-39bf-4889-92bc-45761a726a2d";
    const priceINR = p ? (p.price_cents / 100).toFixed(2) : null;

    const meta: Array<Record<string, string>> = [
      { title: `${name} — Jul's Skin` },
      { name: "description", content: desc },
      { property: "og:title", content: `${name} — Jul's Skin` },
      { property: "og:description", content: desc },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${name} — Jul's Skin` },
      { name: "twitter:description", content: desc },
      { name: "twitter:image", content: image },
    ];

    const scripts: Array<Record<string, any>> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://julsskin.shop/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: "https://julsskin.shop/shop" },
            { "@type": "ListItem", position: 3, name, item: url },
          ],
        }),
      },
    ];
    if (p && priceINR) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          image: [image],
          description: desc,
          sku: p.id,
          category: p.category,
          brand: { "@type": "Brand", name: "Jul's Skin" },
          offers: {
            "@type": "Offer",
            url,
            priceCurrency: "INR",
            price: priceINR,
            availability: "https://schema.org/InStock",
          },
          ...(p.rating && p.reviews_count
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: p.rating,
                  reviewCount: p.reviews_count,
                },
              }
            : {}),
        }),
      });
    }

    return { meta, links: [{ rel: "canonical", href: url }], scripts };
  },
  component: ProductPage,
});

function ProductPage() {
  const { slug } = Route.useParams();
  const [size, setSize] = useState<string>("");
  const [activeImage, setActiveImage] = useState<string>("");
  const { has, toggle } = useWishlist();


  const { data: product, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").eq("slug", slug).single();
      if (error) throw error;
      return data;
    },
  });

  const { data: related } = useQuery({
    queryKey: ["related", product?.category, product?.id],
    enabled: !!product,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, slug, name, category, image_url")
        .eq("category", product!.category)
        .neq("id", product!.id)
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  const gallery = product
    ? [product.image_url, ...((product as any).images ?? [])].filter(Boolean).slice(0, 5)
    : [];
  const main = activeImage || gallery[0] || product?.image_url || "";

  useEffect(() => {
    if (gallery.length <= 1) return;
    const id = setInterval(() => {
      setActiveImage((curr) => {
        const c = curr || gallery[0];
        const idx = gallery.indexOf(c);
        return gallery[(idx + 1) % gallery.length];
      });
    }, 3500);
    return () => clearInterval(id);
  }, [gallery.join("|")]);

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      {isLoading || !product ? (
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-muted animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted w-2/3 animate-pulse" />
            <div className="h-4 bg-muted w-1/3 animate-pulse" />
          </div>
        </div>
      ) : (
        <></>
      )}
      {!isLoading && product && (
        <div className="mx-auto max-w-7xl w-full px-6 py-16 grid md:grid-cols-2 gap-12">
          <div className="space-y-3">
            <div className="bg-muted">
              <img src={resolveImage(product.slug, main, 1000)} alt={product.name} width={1000} height={1250} className="w-full h-auto object-cover" loading="eager" fetchPriority="high" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {gallery.map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    className={`h-16 w-16 overflow-hidden rounded border ${main === url ? "border-foreground" : "border-border"} bg-muted`}
                  >
                    <img src={resolveImage(product.slug, url, 120)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="md:sticky md:top-24 md:self-start">
            <p className="eyebrow text-muted-foreground">{product.category}</p>
            <h1 className="display text-4xl md:text-6xl mt-3 leading-[1.05]">{product.name}</h1>

            {(() => {
              const mrp = (product as any).compare_at_price_cents as number | null | undefined;
              const hasDiscount = mrp != null && mrp > product.price_cents;
              const off = hasDiscount ? Math.round(((mrp! - product.price_cents) / mrp!) * 100) : 0;
              return (
                <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                  <span className="text-3xl font-semibold text-foreground">{formatPrice(product.price_cents)}</span>
                  {hasDiscount && (
                    <>
                      <span className="text-lg text-muted-foreground line-through">{formatPrice(mrp!)}</span>
                      <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">{off}% OFF</span>
                    </>
                  )}
                  {hasDiscount && (
                    <span className="w-full text-xs text-emerald-600 font-medium">You save {formatPrice(mrp! - product.price_cents)}</span>
                  )}
                </div>
              );
            })()}

            {(() => {
              const raw = (product.description ?? "").trim();
              const steps = raw
                .split(/\r?\n+/)
                .map((s) => s.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "").trim())
                .filter(Boolean);
              if (steps.length <= 1) {
                return <p className="mt-8 text-muted-foreground leading-relaxed whitespace-pre-line">{raw}</p>;
              }
              return (
                <div className="mt-8 rounded-2xl border border-border bg-card/60 p-5 md:p-6">
                  <p className="eyebrow text-muted-foreground mb-4">Step by step</p>
                  <ol className="space-y-3">
                    {steps.map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background text-xs font-medium">
                          {i + 1}
                        </span>
                        <span className="pt-0.5 text-sm leading-relaxed text-foreground/90">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })()}

            <div className="mt-8 flex gap-3">
              <a
                href={`https://wa.me/919345883465?text=${encodeURIComponent(`Hi! I'm interested in ordering:\n\n${product.name}\n\nProduct link: ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 border border-foreground py-4 text-sm text-center hover:bg-foreground hover:text-background"
              >Order on WhatsApp</a>
              <button
                type="button"
                onClick={() => {
                  toggle(product.id);
                  toast.success(has(product.id) ? "Removed from wishlist" : "Added to wishlist");
                }}
                aria-label="Toggle wishlist"
                className={`px-4 border border-foreground hover:bg-foreground hover:text-background flex items-center justify-center ${has(product.id) ? "bg-foreground text-background" : ""}`}
              >
                <Heart className="h-5 w-5" fill={has(product.id) ? "currentColor" : "none"} />
              </button>
              <button
                type="button"
                onClick={async () => {
                  const url = typeof window !== "undefined" ? window.location.href : "";
                  const shareData = { title: product.name, text: `Check out ${product.name}`, url };
                  if (typeof navigator !== "undefined" && (navigator as any).share) {
                    try { await (navigator as any).share(shareData); } catch {}
                  } else {
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success("Link copied to clipboard");
                    } catch {
                      toast.error("Could not share");
                    }
                  }
                }}
                aria-label="Share product"
                className="px-4 border border-foreground hover:bg-foreground hover:text-background flex items-center justify-center"
              >
                <Share2 className="h-5 w-5" />
              </button>
            </div>


          </div>
        </div>
      )}
      {!isLoading && product && <ProductReviews productId={product.id} />}


      {related && related.length > 0 && (
        <section className="mx-auto max-w-7xl w-full px-6 pb-20">
          <div className="flex items-end justify-between mb-6">
            <h2 className="display text-3xl md:text-4xl">You may also like</h2>
            <Link to="/shop" className="text-sm text-muted-foreground hover:text-foreground hidden sm:inline">View all →</Link>
          </div>
          <div className="-mx-6 px-6 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <ul className="flex gap-4 pb-2">
              {related.map((p) => (
                <li key={p.id} className="snap-start shrink-0 w-44 sm:w-56 md:w-64">
                  <Link to="/product/$slug" params={{ slug: p.slug }} className="group block">
                    <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted">
                      <img
                        src={resolveImage(p.slug, p.image_url, 400)}
                        alt={p.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <p className="mt-3 text-sm font-medium truncate">{p.name}</p>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate">{p.category}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <div className="flex-1" />
      <SiteFooter />
    </div>
  );
}

